import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import api from '@/services/api';

export const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token'), [searchParams]);
  
  // Initialize status based on whether token exists
  const [status, setStatus] = useState(() => token ? 'loading' : 'error');
  const [message, setMessage] = useState(() => token ? '' : 'No verification token provided');
  const hasVerified = useRef(false);
  
  const verifyEmail = useCallback(async (verificationToken) => {
    try {
      const response = await api.post('/auth/verify-email', { token: verificationToken });
      setStatus('success');
      setMessage(response.data.message || 'Email verified successfully!');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Verification failed';
      if (detail.includes('expired')) {
        setStatus('expired');
      } else if (detail.includes('already verified')) {
        setStatus('success');
      } else {
        setStatus('error');
      }
      setMessage(detail);
    }
    // ``api`` is a module-scope import and ``setStatus`` / ``setMessage``
    // are stable state setters, so no reactive dependencies are needed.
  }, []);
  
  useEffect(() => {
    if (!token || hasVerified.current) return;
    
    hasVerified.current = true;
    verifyEmail(token);
  }, [token, verifyEmail]);
  
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="bg-white border-slate-200 shadow-lg max-w-md w-full">
        <CardContent className="pt-8 pb-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-6" />
              <h1 className="text-xl font-semibold text-slate-900 mb-2">Verifying your email...</h1>
              <p className="text-slate-600">Please wait while we verify your email address.</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h1 className="text-xl font-semibold text-slate-900 mb-2">Email Verified!</h1>
              <p className="text-slate-600 mb-6">{message}</p>
              <Button 
                onClick={() => navigate('/dashboard')}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-xl font-semibold text-slate-900 mb-2">Verification Failed</h1>
              <p className="text-slate-600 mb-6">{message}</p>
              <Button 
                onClick={() => navigate('/login')}
                variant="outline"
                className="border-slate-300 text-slate-700 hover:bg-slate-100"
                data-testid="back-to-login-btn"
              >
                Back to Login
              </Button>
            </>
          )}
          
          {status === 'expired' && (
            <>
              <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
                <Mail className="w-10 h-10 text-amber-600" />
              </div>
              <h1 className="text-xl font-semibold text-slate-900 mb-2">Link Expired</h1>
              <p className="text-slate-600 mb-6">
                This verification link has expired. Please request a new one.
              </p>
              <div className="flex gap-3 justify-center">
                <Button 
                  onClick={() => navigate('/login')}
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-100"
                >
                  Back to Login
                </Button>
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Resend Verification
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmailPage;
